import { Box, Typography } from "@mui/material";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, LAYOUT_OFFSET, LETTER_SPACING } from "../../../theme/tokens";
import { SystemSwitch } from "../../UIToolkit/Clickable/SystemSwitch";
import { type SystemChipItem, SystemChips } from "../../UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { InHouseMobileSheet } from "../../UIToolkit/Popups/InHouseMobileSheet";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import type { GapsDayFilter } from "./gapsDayModel";

type Props = {
	open: boolean;
	onClose: () => void;
	available: { jobLabels: string[]; locationNames: string[]; timeLabels: string[] };
	filter: GapsDayFilter;
	onChange: (next: GapsDayFilter) => void;
	onClear: () => void;
	isSaved: boolean;
	onSavedChange: (next: boolean) => void;
	/**
	 * Offset so the sheet stops ABOVE the bottom bar rather than under it — the paper and the scrim both, so the
	 * bar stays lit and tappable while the sheet is up (D5). Navigation that a sheet can cover is a toolbar.
	 */
	bottomOffset: string;
};

type Dimension = keyof GapsDayFilter;

/**
 * The "All" chip's value. A `Symbol`, so it can never collide with a real job, unit or shift label —
 * `SystemChips` matches selection by strict equality, which a symbol satisfies and a lookalike string
 * never could.
 */
const ALL_CHIP: unique symbol = Symbol("gaps-filter-all");
type ChipValue = string | typeof ALL_CHIP;

const rowLabelSx = {
	fontSize: FONT_SIZE.LABEL_XS,
	letterSpacing: LETTER_SPACING.LABEL,
	textTransform: "uppercase",
	fontWeight: 600,
	color: INK.MUTED,
	mb: 0.5,
} as const;

/**
 * Job, unit and shift window — three rows that narrow the list independently.
 *
 * Nothing here is a mode: each row is a set of things to include, and leaving a row alone means the
 * whole of it. A dimension is stored as `undefined` rather than as "every value selected", so the
 * screen can tell "show me everything" apart from "I happened to tick them all" — only the first
 * keeps working when a unit is added to the department later.
 */
export const GapsFilterSheet: FC<Props> = ({ open, onClose, available, filter, onChange, onClear, isSaved, onSavedChange, bottomOffset }) => {
	const { t } = useTranslation();

	const selectionFor = useCallback((dimension: Dimension, all: string[]): string[] => Array.from(filter[dimension] ?? all), [filter]);

	const handleToggle = useCallback(
		(dimension: Dimension, all: string[], value: string, selected: boolean): void => {
			const current = new Set(filter[dimension] ?? all);
			if (selected) current.add(value);
			else current.delete(value);
			// Everything ticked is not a filter — collapse it back to `undefined` so the screen stops
			// reading as narrowed and a later-added unit is included rather than silently missing.
			const isEverything = current.size === all.length && all.every((item) => current.has(item));
			onChange({ ...filter, [dimension]: isEverything ? undefined : current });
		},
		[filter, onChange],
	);

	const renderRow = useCallback(
		(dimension: Dimension, labelKey: string, all: string[], testId: string) => {
			if (all.length === 0) return null;
			const selected = selectionFor(dimension, all);
			const allItem: SystemChipItem<ChipValue> = { value: ALL_CHIP, label: t("gaps.filter.all") };
			const valueItems: SystemChipItem<ChipValue>[] = all.map((value) => ({ value, label: value }));
			// "All" reads as on exactly when the dimension is unfiltered, as the artifact's row does.
			const isEverything = filter[dimension] === undefined;
			const selectedItems = [
				...(isEverything ? [allItem] : []),
				...valueItems.filter((item) => typeof item.value === "string" && selected.includes(item.value)),
			];
			return (
				<Box data-testid={testId} sx={{ mb: 2 }}>
					<Typography sx={rowLabelSx}>{t(labelKey)}</Typography>
					{/* The chips' own 28px, not the 44px touch floor: they are wrapped rows of separate targets with a
					    gap between them, and the floor made every row half again as tall — the sheet read as a stack of
					    buttons rather than a set of tags. */}
					<SystemChips<ChipValue>
						items={[allItem, ...valueItems]}
						selectedItems={selectedItems}
						onChange={(item, isSelected) => {
							// "All" only ever widens: tapping it restores the whole dimension, never empties it.
							if (item.value === ALL_CHIP) {
								onChange({ ...filter, [dimension]: undefined });
								return;
							}
							handleToggle(dimension, all, item.value, isSelected);
						}}
						dataTestId={`${testId}-chips`}
						getItemDataTestId={(item) => `${testId}-chip-${item.value === ALL_CHIP ? "all" : item.value}`}
						rootSx={{ justifyContent: "flex-start" }}
					/>
				</Box>
			);
		},
		[selectionFor, handleToggle, filter, onChange, t],
	);

	// The controls that end the task live in the sheet's sticky footer rather than under the chips. The
	// sheet is swipe-to-dismiss, and `swipeToClose` gives up native scrolling inside the paper — so on a
	// department with enough units to overflow it, anything below the chips could not be reached.
	//
	// Compact on purpose: the footer is pinned, so every pixel it takes is one the chip rows lose, and
	// two full-width buttons are already a large target without the 44px floor stacked under them.
	const footer = (
		<Box data-testid="gaps-filter-sheet-footer">
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
				<Typography sx={{ flex: 1, fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>{t("gaps.filter.saveAsDefault")}</Typography>
				<SystemSwitch
					checked={isSaved}
					onChange={(event) => onSavedChange(event.target.checked)}
					ariaLabel={t("gaps.filter.saveAsDefault")}
					dataTestId="gaps-filter-save-default"
				/>
			</Box>
			<Box sx={{ display: "flex", gap: 1 }}>
				<SystemButton buttonVariant="secondary" size="compact" onClick={onClear} data-testid="gaps-filter-clear" sx={{ flex: 1 }}>
					{t("gaps.filter.showEverything")}
				</SystemButton>
				<SystemButton buttonVariant="primary" size="compact" onClick={onClose} data-testid="gaps-filter-done" sx={{ flex: 1 }}>
					{t("gaps.filter.done")}
				</SystemButton>
			</Box>
		</Box>
	);

	return (
		<InHouseMobileSheet
			open={open}
			onClose={onClose}
			title={t("gaps.filter.title")}
			showHeader
			swipeToClose
			showHandle
			footer={footer}
			dataTestId="gaps-filter-sheet"
			fitContent
			// Sized to its chips, and allowed the whole screen above the bar when they need it (user decision):
			// swipe-to-close gives up scrolling inside the sheet, so height is what keeps every chip reachable.
			maxHeight={`calc(100dvh - ${bottomOffset} - ${LAYOUT_OFFSET.SHEET_TOP_GAP_CSS})`}
			bottomOffset={bottomOffset}
		>
			<Box data-testid="gaps-filter-sheet-body">
				<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, mb: 1.5 }}>{t("gaps.filter.subtitle")}</Typography>
				{renderRow("jobLabels", "gaps.filter.job", available.jobLabels, "gaps-filter-jobs")}
				{renderRow("locationNames", "gaps.filter.units", available.locationNames, "gaps-filter-units")}
				{renderRow("timeLabels", "gaps.filter.shift", available.timeLabels, "gaps-filter-shifts")}
			</Box>
		</InHouseMobileSheet>
	);
};
